/** Customer-facing progress labels for Kin tool parts. Never expose raw tool names in the UI. */

export const GENERIC_TOOL_PROGRESS_LABEL = 'Checking Kintsugi data';

const MUTATION_TOOL_NAMES = new Set<string>([
  'bulk_classify',
  'bulk_approve',
  'bulk_approve_by_category',
  'update_product',
  'create_product_configs',
  'add_to_blacklist',
  'remove_from_blacklist',
  'approve_product_batch',
  'filings_approve_filing',
  'filings_pause_filing',
  'transaction_import_import_file',
  'transaction_import_import_valid_rows',
  // address_validation mutators — keep in sync with agent use_cases/*/mutating_tool_names
  'address_validation_approve_batch',
  'address_validation_update_addresses',
]);

/** Exact toolName → active label. Prefer exact matches before prefix rules. */
const EXACT_LABELS: Record<string, string> = {
  org_get_nexus_list: 'Checking nexus exposure',
  dashboard_get_nexus_list: 'Checking nexus exposure',
  monitoring_get_nexus_list: 'Checking nexus exposure',
  org_get_physical_nexus_list: 'Checking physical presence',
  monitoring_get_physical_nexus_list: 'Checking physical presence',
  org_get_registrations: 'Checking registrations',
  monitoring_get_registrations: 'Checking registrations',
  registrations_get_registrations: 'Checking registrations',
  org_get_filings: 'Checking filings',
  org_get_filings_metrics: 'Checking filings',
  monitoring_get_filings: 'Checking filings',
  monitoring_get_filings_metrics: 'Checking filings',
  filings_get_filings: 'Checking filings',
  filings_get_filings_metrics: 'Checking filings',
  filings_get_transaction_summary: 'Checking filings',
  org_get_transactions: 'Checking transactions',
  transactions_get_transactions: 'Checking transactions',
  customers_get_transactions: 'Checking transactions',
  transactions_estimate_tax: 'Checking transactions',
  customers_get_customers: 'Searching customers',
  get_products: 'Checking your product catalog',
  get_product_categories: 'Checking your product catalog',
  get_product_overview: 'Checking your product catalog',
  get_product_detail: 'Checking your product catalog',
  get_product_summary: 'Checking your product catalog',
  get_classification_status: 'Checking your product catalog',
  get_classification_progress: 'Checking your product catalog',
  connections_get_connections: 'Checking data sources',
  org_get_connections: 'Checking data sources',
  search_tax_research: 'Reviewing tax guidance',
  search_knowledge_base: 'Checking Kintsugi help',
  web_search: 'Reviewing tax guidance',
  reports_request_report: 'Preparing your report',
  reports_get_report_job_status: 'Preparing your report',
  reports_get_report_download: 'Preparing your report',
  address_validation_get_summary: 'Reviewing address issues',
  address_validation_get_addresses: 'Reviewing address issues',
  address_validation_get_approval_preview: 'Reviewing address issues',
  address_validation_parse_address: 'Reviewing address issues',
  org_get_address_summary: 'Reviewing address issues',
  partner_get_clients: 'Searching customers',
  partner_get_filings: 'Checking filings',
  partner_get_filings_summary: 'Checking filings',
  partner_get_filing_jurisdictions: 'Checking filings',
  partner_get_registrations: 'Checking registrations',
  partner_get_registrations_summary: 'Checking registrations',
  partner_get_nexus_summary: 'Checking nexus exposure',
  partner_get_nexus_report: 'Checking nexus exposure',
  partner_get_client_nexus_detail: 'Checking nexus exposure',
  partner_get_customers: 'Searching customers',
  partner_get_transactions: 'Checking transactions',
  partner_get_connections: 'Checking data sources',
  partner_get_products: 'Checking your product catalog',
  partner_request_report: 'Preparing your report',
};

const PREFIX_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: 'dashboard_request_report', label: 'Preparing your report' },
  { prefix: 'monitoring_request_report', label: 'Preparing your report' },
  { prefix: 'filings_request_report', label: 'Preparing your report' },
  { prefix: 'registrations_request_report', label: 'Preparing your report' },
  { prefix: 'transactions_request_report', label: 'Preparing your report' },
  { prefix: 'customers_request_report', label: 'Preparing your report' },
  { prefix: 'connections_request_report', label: 'Preparing your report' },
  { prefix: 'products_request_report', label: 'Preparing your report' },
  { prefix: 'address_validation_request_report', label: 'Preparing your report' },
];

const COMPLETED_OVERRIDES: Record<string, string> = {
  'Preparing this change…': 'Prepared this change',
  'Preparing your report': 'Prepared your report',
  'Searching customers': 'Searched customers',
  'Reviewing tax guidance': 'Reviewed tax guidance',
  'Reviewing address issues': 'Reviewed address issues',
  'Checking Kintsugi help': 'Checked Kintsugi help',
  'Checking Kintsugi data': 'Checked Kintsugi data',
  'Checking nexus exposure': 'Checked nexus exposure',
  'Checking registrations': 'Checked registrations',
  'Checking physical presence': 'Checked physical presence',
  'Checking filings': 'Checked filings',
  'Checking transactions': 'Checked transactions',
  'Checking your product catalog': 'Checked your product catalog',
  'Checking data sources': 'Checked data sources',
};

export function isMutationToolName(toolName: string): boolean {
  return MUTATION_TOOL_NAMES.has(toolName);
}

export function toolProgressLabel(toolName: string): string {
  const name = toolName.trim();
  if (!name) {
    return GENERIC_TOOL_PROGRESS_LABEL;
  }
  if (isMutationToolName(name)) {
    return 'Preparing this change…';
  }
  const exact = EXACT_LABELS[name];
  if (exact) {
    return exact;
  }
  for (const { prefix, label } of PREFIX_LABELS) {
    if (name.startsWith(prefix) || name === prefix) {
      return label;
    }
  }
  return GENERIC_TOOL_PROGRESS_LABEL;
}

export function toolProgressCompletedLabel(toolName: string): string {
  const active = toolProgressLabel(toolName);
  return COMPLETED_OVERRIDES[active] ?? active.replace(/^Checking /, 'Checked ').replace(/…$/, '');
}
